import { useEffect, useRef, useState } from "react";
import { X, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

/**
 * EncourageOwnRenderModal
 * -----------------------
 * Shown once per listing, immediately after the seller's STL upload
 * hits the backend and the studio renders start generating in the
 * background. Nudges the seller to upload their own hero renders,
 * because a good render dramatically outperforms an auto one on the
 * marketplace.
 *
 * Two exits:
 *   • "Upload my render images" — opens the file picker DIRECTLY inside
 *     the modal (no closing-and-hunting for a hidden button). Multiple
 *     files uploaded serially with per-file progress. Each successful
 *     upload lands as a `source=manual` photo — the FIRST also auto-
 *     promotes to cover (see backend upload endpoint).
 *   • "Skip for now" — closes the modal for this listing only. The
 *     seller can still add own images later from the listing manager.
 *
 * Also carries a "Don't show this again" checkbox that flips the
 * user pref `hide_encourage_own_render:true` via PATCH /me/prefs, so
 * repeat sellers who never bother aren't nagged on every listing.
 *
 * Responsive: centred modal on ≥sm, bottom sheet on phones.
 */
export default function EncourageOwnRenderModal({
  listingId,
  onClose,
  onUploaded,
  goodExample = `${process.env.PUBLIC_URL || ""}/render-example-seller.jpg`,
  autoExample = `${process.env.PUBLIC_URL || ""}/render-example-auto.jpg`,
}) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [hideForever, setHideForever] = useState(false);
  const fileInputRef = useRef(null);

  // Keyboard escape → skip
  useEffect(() => {
    const on = (e) => { if (e.key === "Escape") close("esc"); };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const close = async (via) => {
    if (hideForever) {
      try { await api.patch("/me/prefs", { hide_encourage_own_render: true }); } catch {}
    }
    onClose?.(via);
  };

  const pickFiles = () => fileInputRef.current?.click();

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    let ok = 0, fail = 0;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setProgress(`Uploading ${i + 1}/${files.length} · ${f.name}`);
      if (f.size > 8 * 1024 * 1024) {
        toast.error(`${f.name} is over 8 MB — skipping.`);
        fail++;
        continue;
      }
      try {
        const form = new FormData();
        form.append("file", f);
        await api.post(`/seller/listings/${listingId}/photos/upload`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok++;
      } catch (err) {
        toast.error(`${f.name}: ${apiErr(err)}`);
        fail++;
      }
    }
    setBusy(false);
    setProgress("");
    if (ok > 0) {
      toast.success(`${ok} render image${ok === 1 ? "" : "s"} added to your listing.`);
      onUploaded?.(ok);
      close("uploaded");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-slate-950/70 backdrop-blur-sm flex sm:items-center justify-center items-end"
      onClick={(e) => { if (e.target === e.currentTarget) close("backdrop"); }}
      data-testid="encourage-own-render-modal"
    >
      <div
        className="bg-background border-t sm:border border-border shadow-2xl w-full sm:max-w-2xl sm:rounded-xl overflow-hidden sm:my-6 max-h-[92vh] flex flex-col"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start justify-between p-5 border-b border-border">
          <div>
            <h2 className="font-display text-xl sm:text-2xl font-bold">Add your own render images and sell more</h2>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              Jeweltix automatically generates 8 studio photos for every design you upload. But if you have your own rendered images, upload them too — they will be shown first, and they will be what buyers see on the marketplace.
            </p>
          </div>
          <button
            onClick={() => close("x")}
            className="text-muted-foreground hover:text-foreground p-1 -m-1 flex-shrink-0"
            aria-label="Close"
            data-testid="encourage-close-x"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 flex-1 overflow-y-auto space-y-4">
          <p className="text-sm">
            <b>The better the rendering, the better the sales.</b> A high-quality render of your design attracts far more buyers than a standard studio photo. If you have spent time rendering your work, show it.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <ExampleCard src={goodExample} label="With your render" tone="primary" />
            <ExampleCard src={autoExample} label="Auto-generated only" tone="muted" />
          </div>

          <p className="text-[11px] text-muted-foreground text-center">
            Recommended: square images, at least <b>1200 × 1200 px</b>, showing the design clearly against a clean background. JPG · PNG · WebP up to 8 MB each. Up to 10 images per listing.
          </p>

          {progress && (
            <div className="text-xs text-primary flex items-center gap-2" data-testid="encourage-progress">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> {progress}
            </div>
          )}
        </div>

        <div className="p-5 border-t border-border space-y-3 bg-secondary/20">
          <label className="flex items-center gap-2 text-xs text-muted-foreground select-none">
            <input
              type="checkbox"
              checked={hideForever}
              onChange={(e) => setHideForever(e.target.checked)}
              data-testid="encourage-hide-forever"
              className="rounded"
            />
            Don't show this again
          </label>
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
            <button
              onClick={() => close("skip")}
              disabled={busy}
              data-testid="encourage-skip-btn"
              className="border border-border rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-secondary disabled:opacity-60"
            >
              Skip for now
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={handleFiles}
              className="hidden"
              data-testid="encourage-file-input"
            />
            <button
              onClick={pickFiles}
              disabled={busy}
              data-testid="encourage-upload-btn"
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-md px-4 py-2.5 text-sm font-bold shadow hover:bg-primary/90 disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Upload my render images
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExampleCard({ src, label, tone }) {
  const ring = tone === "primary"
    ? "border-primary shadow-lg ring-2 ring-primary/30"
    : "border-border opacity-80";
  return (
    <div className={`border-2 rounded-md overflow-hidden ${ring}`}>
      <img
        src={src}
        alt={label}
        className="w-full aspect-square object-cover bg-slate-50"
        loading="lazy"
        onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
      />
      <div className={`text-[10px] font-mono uppercase tracking-widest text-center py-1.5 ${tone === "primary" ? "text-primary" : "text-muted-foreground"}`}>
        {label}
      </div>
    </div>
  );
}
